import React from "react";
import {
  DISTRICT_FILL_OPACITY,
  DISTRICT_HOVER_STROKE,
  DISTRICT_SELECTED_GLOW,
  DISTRICT_STATUS_COLORS,
} from "../BaseMapStyles";
import "../../../styles/turfcutterMapLegend.scss";

export const TURF_CUTTER_TURF_UNASSIGNED = "#9b4dca";
export const TURF_CUTTER_TURF_LASSO_SELECTED = "#008000";

type MapMode = "district" | "turf";

type TurfCutterMapLegendProps = {
  mode: MapMode;
};

type SwatchProps = {
  color: string;
  fillOpacity?: number;
  noStroke?: boolean;
};

type RingSwatchProps = {
  strokeColor: string;
  glow?: boolean;
};

const NEUTRAL_SWATCH_FILL = "rgba(138, 138, 138, 0.35)";

const ClusterSwatch = ({
  color,
  fillOpacity = 0.4,
  noStroke = false,
}: SwatchProps) => (
  <span
    className={`turfcutter-map-legend__swatch${
      noStroke ? " turfcutter-map-legend__swatch--no-stroke" : ""
    }`}
    style={
      {
        "--swatch-color": color,
        "--swatch-fill-opacity": fillOpacity,
      } as React.CSSProperties
    }
    aria-hidden
  />
);

const RingSwatch = ({ strokeColor, glow = false }: RingSwatchProps) => (
  <span
    className={`turfcutter-map-legend__swatch turfcutter-map-legend__swatch--ring${
      glow ? " turfcutter-map-legend__swatch--glow" : ""
    }`}
    style={
      {
        "--swatch-color": strokeColor,
        "--swatch-ring-fill": NEUTRAL_SWATCH_FILL,
      } as React.CSSProperties
    }
    aria-hidden
  />
);

const DISTRICT_STATUS_ROWS = [
  {
    color: DISTRICT_STATUS_COLORS.started,
    label: "Canvassing started",
  },
  {
    color: DISTRICT_STATUS_COLORS.notStarted,
    label: "Not started",
  },
];

const DistrictLegend = () => (
  <>
    {DISTRICT_STATUS_ROWS.map(({ color, label }) => (
      <div className="turfcutter-map-legend__row" key={label}>
        <ClusterSwatch color={color} fillOpacity={DISTRICT_FILL_OPACITY} noStroke />
        <span className="turfcutter-map-legend__label">{label}</span>
      </div>
    ))}
    <div className="turfcutter-map-legend__row">
      <RingSwatch strokeColor={DISTRICT_HOVER_STROKE} />
      <span className="turfcutter-map-legend__label">Hovered</span>
    </div>
    <div className="turfcutter-map-legend__row">
      <RingSwatch strokeColor={DISTRICT_SELECTED_GLOW} glow />
      <span className="turfcutter-map-legend__label">Selected</span>
    </div>
    <p className="turfcutter-map-legend__hint">Circle size = door count</p>
  </>
);

const TurfLegend = () => (
  <>
    <div className="turfcutter-map-legend__row">
      <ClusterSwatch color={TURF_CUTTER_TURF_UNASSIGNED} />
      <span className="turfcutter-map-legend__label">Not in a turf yet</span>
    </div>
    <div className="turfcutter-map-legend__row">
      <span
        className="turfcutter-map-legend__swatch turfcutter-map-legend__swatch--varied"
        aria-hidden
      />
      <span className="turfcutter-map-legend__label">In a turf (turf color)</span>
    </div>
    <div className="turfcutter-map-legend__row">
      <ClusterSwatch color={TURF_CUTTER_TURF_LASSO_SELECTED} fillOpacity={0.8} />
      <span className="turfcutter-map-legend__label">Selected doors</span>
    </div>
  </>
);

const TurfCutterMapLegend = ({ mode }: TurfCutterMapLegendProps) => {
  const title = mode === "district" ? "District clusters" : "Door clusters";

  return (
    <aside
      className="turfcutter-map-legend"
      data-component="TurfCutterMapLegend"
      aria-label="Map cluster legend"
    >
      <div className="turfcutter-map-legend__title">{title}</div>
      {mode === "district" ? <DistrictLegend /> : <TurfLegend />}
    </aside>
  );
};

export default TurfCutterMapLegend;
